import { useDispatch } from 'stimulus-use'
import debounce from 'lodash.debounce'
import omit from 'lodash.omit'
import Rails from '@rails/ujs'
import ApplicationController from '@app/javascript/controllers/application_controller'
import { useBusItem } from '@app/javascript/controllers/mixins/useBusItem.js'

export default class extends ApplicationController {
  static targets = [
    'form',
    'keywordsInput',
    'dateFilter',
    'totalCount',
    'filter',
    'searchResultsList'
  ]

  static values = {
    alertParams: Object,
    defaultSearchParams: Object,
    selectedSavedSearch: String,
    selectedAlert: String,
    selectedInbox: String,
    skipActiveFiltersPills: Array,
    searchResultsUrl: String,
    filtersOnlyMode: Boolean,
    baseUrl: String
  }

  doConnect() {
    useDispatch(this)
    useBusItem(this)

    this.reloadResults = debounce(this.reloadResults, 1000, { leading: false })
    this.isSavedSearch = !!this.selectedSavedSearchValue
    this.isInbox = !!this.selectedInboxValue
    this.isAlert = !!this.selectedAlertValue
    this.isFiltersOnlyMode = !!this.filtersOnlyModeValue

    if (this.isSavedSearch) {
      this.reloadResults()
    } else {
      this.reloadResults(null, { urlParamsUpToDate: true })
    }
  }

  afterConnect() {
    this.dispatch('searchConnected', {
      paramsObj: this.getCurrentSearchParamsFromURL()
    })
  }

  navigate(event) {
    event.preventDefault()

    const targetURL = event.target.closest('a').href
    this.updateRailsUrl(targetURL, true)
    location.reload()
  }

  updateRailsUrl(navigatingToUrl = null, startFresh = false) {
    if (!window.history.pushState) return

    let href, params
    const { origin, pathname, search } = new URL(window.location.href)

    if (navigatingToUrl) {
      const parsedUrl = new URL(navigatingToUrl)
      // if link_to URL contains pre-defined params then merge it with existing param
      if (parsedUrl.search) {
        params = { ...$.deparam(search.substr(1)), ...$.deparam(parsedUrl.search.substr(1)) }
        params = $.param(params)
      }
      href = parsedUrl.origin + parsedUrl.pathname
    } else {
      href = origin + pathname
    }

    if (!params && !startFresh) {
      const currentSearchState = this.getCurrentPersistableSearchState()
      const formData = currentSearchState && $.param(currentSearchState)

      params = formData
    }

    const newURL = params ? `${href}?${params}` : href
    window.history.pushState({ path: newURL }, '', newURL)
    // update ember URL
    this.dispatch('updateEmberUrl', {
      newURL
    })
  }

  sortingTriggered(event) {
    event.preventDefault()

    this.reloadResults(null, { refreshActiveFilters: false, triggeredBy: 'sortingChanged' })
  }

  keywordsChanged(event) {
    event.preventDefault()

    // Dispatch event sync'ed - important
    this.dispatch('keywordsChanged', {
      searchKeywords: this.keywordsInputTarget.value,
      paramsObj: this.getCurrentSearchParams(),
      persistableSearchState: this.getCurrentPersistableSearchState()
    })

    this.reloadResults(null, { refreshActiveFilters: false, triggeredBy: 'criteriaChanged' })
  }

  filterTriggered(event) {
    event.preventDefault()

    // Dispatch event sync'ed - important
    this.dispatch('filterTriggered', {
      paramsObj: this.getCurrentSearchParams(),
      persistableSearchState: this.getCurrentPersistableSearchState()
    })

    this.reloadResults(null, { triggeredBy: 'criteriaChanged' })
  }

  async reloadResults(_event, opts = {}) {
    this.generateResultsId()

    if (this.isFiltersOnlyMode) {
      this.pretendReloadingResults()
    } else if (this.isInbox) {
      this.reloadInboxResults()
    } else if (this.isAlert) {
      this.reloadAlertResults()
    } else {
      this.reloadSearchResults(opts)
    }
  }

  // A results ID avoids stale results responses getting rendered on top of the
  // most up-to-date results response.
  generateResultsId() {
    this.resultsId = new Date().getTime()

    if (this.hasSearchResultsListTarget) {
      this.searchResultsListTarget.setAttribute('data-resid', this.resultsId)
    }
  }

  async pretendReloadingResults() {
    const params = this.getCurrentSearchParams()

    this.beforeReloadResults({ updateUrl: false, refreshActiveFilters: true }, params)
    this.afterReloadResults()
  }

  async reloadSearchResults(opts) {
    const { urlParamsUpToDate } = opts

    // Purposely not using currentSearchState here
    const params = urlParamsUpToDate
      ? this.getCurrentSearchParamsFromURL()
      : this.getCurrentSearchParams()

    this.beforeReloadResults({ updateUrl: !urlParamsUpToDate, ...opts }, params)
    Rails.ajax({
      type: 'GET',
      url: this.searchResultsUrlValue || '/search',
      dataType: 'script',
      data: $.param({ ...params, _resid: this.resultsId }),
      complete: () => this.afterReloadResults()
    })
  }

  async reloadInboxResults() {
    const params = this.getCurrentSearchParams()

    this.beforeReloadResults({ updateUrl: false, refreshActiveFilters: false })

    Rails.ajax({
      type: 'GET',
      url: `/search/inboxes/${this.selectedInboxValue}`,
      dataType: 'script',
      data: $.param({ ...params, _resid: this.resultsId }),
      complete: () => this.afterReloadResults()
    })
  }

  async reloadAlertResults() {
    const params = this.getCurrentSearchParams()

    this.beforeReloadResults({ updateUrl: false, refreshActiveFilters: true })
    Rails.ajax({
      type: 'GET',
      url: `/search/alerts/${this.selectedAlertValue}`,
      dataType: 'script',
      data: $.param({ ...params, _resid: this.resultsId }),
      complete: () => this.afterReloadResults()
    })
  }

  beforeReloadResults({ updateUrl = true, refreshActiveFilters = true, triggeredBy }, params = null) {
    params ||= this.getCurrentSearchParams()

    if (updateUrl) this.updateRailsUrl()
    if (refreshActiveFilters) this.refreshActiveFilters(params)

    // Dispatch events async - important
    setTimeout(() => {
      this.dispatch('resultsLoading')
      this.dispatch('searchTriggered', { params: $.param(params), paramsObj: params, triggeredBy })
    })
  }

  afterReloadResults() {
    const totalCount = this.hasTotalCountTarget ? this.totalCountTarget.dataset.value : 0

    this.dispatch('resultsLoaded', { totalCount })
    this.getUserdata()
  }

  async loadMoreResults(event) {
    event.preventDefault()

    const searchParams = this.getCurrentSearchParams()
    const continuationToken = event.currentTarget.getAttribute('data-continuation-token')

    // To load more results, we need to pass all the current search params together
    const loadMoreParams = {
      ...searchParams,
      continuation_token: continuationToken,
      inbox_id: this.selectedInboxValue,
      alert_id: this.selectedAlertValue
    }

    Rails.ajax({
      type: 'POST', // POST instead of GET because the continuation token can be pretty long
      url: '/search/load_more_results',
      dataType: 'script',
      data: $.param(loadMoreParams),
      complete: () => this.afterReloadResults()
    })
  }

  async refreshActiveFilters(searchParams) {
    const skippedFilters = this.skipActiveFiltersPillsValue || []
    const activeFilterParams = omit(searchParams, skippedFilters)

    Rails.ajax({
      type: 'GET',
      url: '/search/active_filters',
      dataType: 'script',
      data: $.param({ ...activeFilterParams, readonly: this.isInbox, skippedFilters })
    })

    Rails.ajax({
      type: 'GET',
      url: '/search/alert_filters',
      dataType: 'script',
      data: $.param({ ...this.alertParamsValue, skippedFilters })
    })

    Rails.ajax({
      type: 'GET',
      url: '/search/quick_filters?base_url=' + this.baseUrlValue,
      dataType: 'script',
      data: $.param({ ...activeFilterParams, readonly: this.isInbox, skippedFilters })
    })
  }

  clearSearchTextInput() {
    this.element.querySelectorAll('input[name="search_text"]').forEach((el) => {
      el.value = ''
    })
  }

  clearAllFilters() {
    this.getBusItem('filters-container')?.clearAll()
  }

  // Returns the current search params from the underlying search <form> elements.
  // Note that this is different than the current search state.
  getCurrentSearchParams() {
    if (!this.hasFormTarget) return {}

    const params = this.serializeSearchForms()

    return { ...this.defaultSearchParams, ...params }
  }

  // Returns the current search params from the URL's query string.
  getCurrentSearchParamsFromURL() {
    const { search } = new URL(window.location.href)
    const params = $.deparam(search.substr(1))

    return { ...this.defaultSearchParams, ...params }
  }

  // Returns the current search params state, which are the permitted params that
  // can/should be PERSISTED (as a saved search) and that can be SHARED (via the URL).
  // For actually executing the search, we want to use the whole form data.
  getCurrentPersistableSearchState() {
    const params = this.getCurrentSearchParams()

    if (this.hasDateFilterTarget) {
      this.dateFilterTargets.forEach((dateFilter) => {
        const dateFilterOption = dateFilter.getAttribute('data-daterange-option-param')
        const startDate = dateFilter.getAttribute('data-start-date-param')
        const endDate = dateFilter.getAttribute('data-end-date-param')

        // When the selected date range is a predefined range (eg Last X Days), we want
        // the actual startDate and endDate dates to always be resolved at runtime.
        // For that reason, we don't want to store the current values, thus removing the
        // corresponding start/end_date from the params.
        if (params[dateFilterOption] !== 'custom-range') {
          delete params[startDate]
          delete params[endDate]
        }
      })
    }

    return params
  }

  serializeSearchForms() {
    const formsData = this.formTargets.map(form => $.deparam($(form).serialize()))
    const formsDataMerged = Object.assign({}, ...formsData)
    return this.cleanParams(formsDataMerged)
  }

  cleanParams(params) {
    const isEmptyArray = (arr) => {
      // checks whether the array is empty or consisting completely of 'empty/blank' elements
      return Array.isArray(arr) ? !arr.find((item) => item) : false
    }

    for (const param in params) {
      if (!params[param]) delete params[param]
      if (isEmptyArray(params[param])) delete params[param]
    }

    // The param 'labels_logical_operator' makes sense only when there's a label selected.
    if (params.labels_logical_operator && !params.labels_ids) {
      delete params.labels_logical_operator
    }

    return params
  }

  get defaultSearchParams() {
    // Workaround to include the sorting params in the default search params,
    // since they don't come in the value object anymore.
    const { size, sort_order, sort_field } = this.serializeSearchForms() // eslint-disable-line

    return this.cleanParams({ ...this.defaultSearchParamsValue, size, sort_order, sort_field })
  }

  getUserdata() {
    const entityIds = []

    document.querySelectorAll('[data-userdata-id]').forEach((userdataContainer) => {
      const id = userdataContainer.getAttribute('data-userdata-id')
      const loaded = userdataContainer.dataset.loaded?.toString() === 'true'

      if (!loaded) entityIds.push(id)
    })

    if (entityIds.length === 0) {
      return
    }

    Rails.ajax({
      type: 'GET',
      url: '/search/userdata',
      dataType: 'script',
      data: $.param({ ids: entityIds })
    })
  }
}
